import {
  Button,
  confirm,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { FormListItemDTO } from "@/modules/forms/presentation/api/types";
import { FormStatusBadge } from "@/modules/forms/presentation/ui/FormStatusBadge";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type DeadlineSettings = {
  deadline?: string;
};

function deadlineDisplay(settings: unknown): string {
  if (!settings || typeof settings !== "object") return "—";
  const raw = (settings as DeadlineSettings).deadline?.trim?.();
  if (!raw) return "—";
  return formatDateTime(raw);
}

type FormsTableProps = {
  data: FormListItemDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  publicBaseUrl?: string;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (id: string) => void;
  onResponses: (id: string) => void;
  onDelete?: (id: string) => void;
};

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}

export function FormsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  publicBaseUrl = typeof window !== "undefined" ? window.location.origin : "",
  canUpdate,
  canDelete,
  onEdit,
  onResponses,
  onDelete,
}: FormsTableProps) {
  const columns: TanstackReactTable.ColumnDef<FormListItemDTO>[] = [
    {
      id: "title",
      header: "ຫົວຂໍ້",
      accessorKey: "title",
      enableSorting: false,
      cell: ({ row }) => {
        const f = row.original;
        const link = (
          <Link
            to="/app/forms/$id/edit"
            params={{ id: f.id }}
            className="font-medium text-primary hover:underline"
          >
            {f.title}
          </Link>
        );
        const node: ReactNode = canUpdate ? (
          link
        ) : (
          <Link
            to="/app/forms/$id/responses"
            params={{ id: f.id }}
            className="font-medium hover:underline"
          >
            {f.title}
          </Link>
        );
        return <div className="max-w-xs truncate">{node}</div>;
      },
    },
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      enableSorting: false,
      cell: ({ row }) => <FormStatusBadge status={row.original.status} />,
    },
    {
      id: "category",
      header: "ຫມວດຫມູ່",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.categoryName?.trim() || "—"}
        </span>
      ),
    },
    {
      id: "deadline",
      header: "ເສັ້ນກຳນົດ",
      enableSorting: false,
      cell: ({ row }) => deadlineDisplay(row.original.settings ?? null),
    },
    {
      accessorKey: "responseCount",
      header: "ຄຳຕອບ",
      enableSorting: false,
      cell: ({ row }) => row.original.responseCount,
    },
    {
      id: "links",
      header: "ລິ້ງໜ້າ public",
      enableSorting: false,
      cell: ({ row }) => {
        const f = row.original;
        const href = `${publicBaseUrl}/f/${f.id}`;
        const isLive = f.status === "published";
        return (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!isLive}
              title={
                !isLive ? "ຕ້ອງເປີດຟອມເປັນ published ກ່ອນຈະສຳເນົາລິ້ງໄດ້" : undefined
              }
              onClick={async () => {
                await copyText(href);
                toast.success("ສຳເນົາລິ້ງແລ້ວ");
              }}
            >
              ສຳເນົາລິ້ງ public
            </Button>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        const f = row.original;
        const actions = [];
        actions.push({
          label: "ຄຳຕອບ",
          onClick: () => onResponses(f.id),
        });
        if (canUpdate) {
          actions.push({
            label: "ແກ້ໄຂ",
            onClick: () => onEdit(f.id),
          });
        }
        if (canDelete && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive" as const,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຟອມ",
                description: "ຫຼັງລຶບບໍ່ສາມາດກູ້ຄືນ ທັງເອກະສານຟອມ ແລະ ຄຳຕອບທັງໝົດ.",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(f.id);
            },
          });
        }
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<FormListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບຟອມໃດໆ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy="title"
      sortOrder="asc"
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      onSortingChange={(sorting) => {
        if (!sorting?.length) return;
      }}
    />
  );
}
